/*
 * Copyright (c) SEQTA Software 2018.
 *
 * Created by simon on 2018-04-30
 */

import React, {Component} from 'react';
import FormFieldList from '../widgets/FormFieldList';
import FormField from '../widgets/FormField';
import Input from '../widgets/Input';
import Pillbox from '../widgets/Pillbox';
import Main from '../widgets/Main';
import ImageControl from '../widgets/ImageControl';
import {connect} from 'react-redux';
import Actions from '../widgets/Actions';
import {actions} from './users.state'
import Loading from '../widgets/Loading';
import {actions as dirtyActions} from '../dirty.state';
import isValidEmail from '../isValidEmail';
import jsonFetch from '../jsonFetch';
import {admin_syllabus, admin_user} from '../permissions';
import SubtleMessage from '../widgets/SubtleMessage';
import {actions as errorActions} from '../error.state';
import {getImagePublicPath} from '../imageUtil';

export class UsersPage extends Component {
	constructor(props) {
		super();
		
		this.state = {lastSave: null};
		
		/**
		 * Track last save time so we can show message after save.
		 */
		this.save = action => {
			this.props.save(action);
			if (action === 'Save') this.setState({lastSave: new Date().getTime()});
		};
		
		this.onDrop = files => {
			const file = files[0];
			
			if (!file || !file.type) return; // Didn't upload anything
			
			const xhr = new XMLHttpRequest();
			const imageUploadUrl = this.props.user.imageUploadUrl;
			
			xhr.upload.addEventListener('progress', e => {
				if (e.lengthComputable) {
					// TODO Would be nice to show progress in the UI
					console.log((e.loaded / e.total) * 100);
				}
			});
			
			xhr.upload.addEventListener('load', () => {
				// New image path is everything left of query params
				this.props.updateImagePath(getImagePublicPath(imageUploadUrl));
				this.retrieveUploadUrl(this.props.user.email);
			});
			
			xhr.open('PUT', imageUploadUrl);
			xhr.overrideMimeType(file.type);
			xhr.setRequestHeader('Cache-Control', 'max-age=7776000');
			
			xhr.send(file);
			this.props.clearUploadUrl(); // Needs invalidation for next upload
		};
		
		this.onEmailChange = e => this.props.userNewUpdateEmail(e.target.value);
		this.onFirstnameChange = e => this.props.updateFirstname(e.target.value);
		this.onLastnameChange = e => this.props.updateLastname(e.target.value);
		this.retrieveUploadUrl = email => {
			props.clearUploadUrl();
			// WARNING this.props may not be updated yet (if called from WillReceiveProps). It's important to this.props in
			// the callback as it would have updated by then and thus will have closed over the latest setID.
			jsonFetch(`${process.env.REACT_APP_API}/user/${email || 'new'}/image-upload-url`)
				.then(json => this.props.updateImageUploadUrl(json.imageUploadUrl));
		};
		const permissionUpdater = permission => option => {
			if (option === 'On' && !this.props.user.permissions.includes(permission)) {
				this.props.updatePermissions([...this.props.user.permissions, permission]);
			} else if (option === 'Off' && this.props.user.permissions.includes(permission)) {
				this.props.updatePermissions(this.props.user.permissions.filter(item => item !== permission));
			}
		};
		this.onChangeAdminSyllabus = permissionUpdater(admin_syllabus);
		this.onChangeAdminUsers = permissionUpdater(admin_user);
		
		if (props.user && props.user.email) {
			this.retrieveUploadUrl(props.user.email);
		}
	}
	
	componentWillReceiveProps(nextProps) {
		// Retrieve new image URL if the user changes
		if (nextProps.user && (
			!this.props.user // Previously no user was set
			|| (nextProps.user.isNew !== this.props.user.isNew) // Is now a new user, or was previously a new user
			|| (!nextProps.user.isNew && nextProps.user.email !== this.props.user.email) // User has changed (email key)
		)) {
			this.retrieveUploadUrl(nextProps.user.email);
		}
	}
	
	render() {
		if (this.props.isRetrieving) {
			return (<Loading />);
		}
		
		if (!this.props.user) return null;
		
		const {
			user: {email, firstname, lastname, imagePath, permissions, imageUploadUrl, isNew = false},
			save, isSaving, isDirty, onUploadError
		} = this.props;
		
		const emailValid = !isNew || isValidEmail(email);
		
		return (
			<Main>
				<section className="UsersPage">
					<FormFieldList>
						<FormField label="Email address" explanation={isNew ? '' : (
							<p>NOTE: Emails cannot be changed after creation. Please create a new user instead.</p>
						)}>
							{/* Can only update when creating */}
							<Input
								className={emailValid ? '' : 'invalid'}
								type="text"
								value={email}
								onChange={this.onEmailChange}
								disabled={!isNew}
								maxLength={512}
							/>
						</FormField>
						<FormField label="First name">
							<Input type="text" maxLength={256} value={firstname} onChange={this.onFirstnameChange} />
						</FormField>
						<FormField label="Last name">
							<Input type="text" maxLength={256} value={lastname} onChange={this.onLastnameChange} />
						</FormField>
						<FormField label="Photo">
							<ImageControl
								disabled={!imageUploadUrl}
								onDrop={this.onDrop}
								onDropRejected={onUploadError}
								backgroundUrl={imagePath}
							/>
						</FormField>
						<FormField label="Administer syllabus">
							<Pillbox options={['On', 'Off']} onSelect={this.onChangeAdminSyllabus} selected={permissions.indexOf(admin_syllabus) >= 0 ? 'On' : 'Off'} />
						</FormField>
						<FormField label="Administer users">
							<Pillbox options={['On', 'Off']} onSelect={this.onChangeAdminUsers} selected={permissions.indexOf(admin_user) >= 0 ? 'On' : 'Off'}/>
						</FormField>
					</FormFieldList>
				</section>
				{isSaving ? (<Loading noflex={true} />) : (
					<Actions
						disabledActions={emailValid ? [] : ['Save']}
						isDirty={isDirty}
						actions={['Cancel', 'Save']}
						positive="Save"
						onClick={this.save}
					/>
				)}
				{/*Key is important to ensure new message renders for each save*/}
				{this.state.lastSave ? <SubtleMessage key={this.state.lastSave} message="User details saved. Please re-login for permission changes."/> : undefined}
			</Main>
		);
	}
}

const ConnectedUsersPage = connect(
	({users, dirty}, {match: {params: {email}}}) => ({
		user: users.items.find(user => email === 'new' ? user.isNew : user.email === email),
		isRetrieving: users.retrieving,
		isSaving: users.saving === email,
		isDirty: !!(dirty && dirty.keys && dirty.keys[email])
	}),
	(dispatch, {match: {params: {email}}}) => ({
		clearUploadUrl: () => dispatch(actions.userUpdate({email, imageUploadUrl: null})),
		userNewUpdateEmail: newEmail => {
			dispatch(actions.userNewUpdateEmail(newEmail));
			dispatch(dirtyActions.dirtySet(email));
		},
		updateImageUploadUrl: imageUploadUrl => dispatch(actions.userUpdate(email === 'new' ? {isNew: true, imageUploadUrl} : {email, imageUploadUrl})),
		updateImagePath: imagePath => {
			dispatch(actions.userUpdate(email === 'new' ? {isNew: true, imagePath} : {email, imagePath}));
			dispatch(dirtyActions.dirtySet(email));
		},
		updateFirstname: firstname => {
			dispatch(actions.userUpdate(email === 'new' ? {isNew: true, firstname} : {email, firstname}));
			dispatch(dirtyActions.dirtySet(email));
		},
		updateLastname: lastname => {
			dispatch(actions.userUpdate(email === 'new' ? {isNew: true, lastname} : {email, lastname}));
			dispatch(dirtyActions.dirtySet(email));
		},
		updatePermissions: permissions => {
			dispatch(actions.userUpdate(email === 'new' ? {isNew: true, permissions} : {email, permissions}));
			dispatch(dirtyActions.dirtySet(email));
		},
		save: action => action === 'Save' ? dispatch(actions.save(email, email === 'new')) : dispatch(actions.cancel(email, email === 'new')),
		onUploadError: () => dispatch(errorActions.errorSet({
			title: 'Failed to upload file',
			message: 'The file failed to upload. Please ensure it\'s less than 1MB in size (i.e. scale down and/or use compression).'
		}))
	})
)(UsersPage);

export default ConnectedUsersPage;


// WEBPACK FOOTER //
// ./src/users/UsersPage.js